import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import { ChargerNotification } from '../models/charger-notification.model';
import { PaginatedResponse } from './base-crud.service';

type SimpleApiRelation<T extends 'name' | 'code'> = {
  id: number;
} & (T extends 'name'
  ? { name: string; code?: never }
  : { code: string; name?: never });

interface ChargerNotificationApiItem extends ChargerNotification {
  company: SimpleApiRelation<'name'> | null;
  station: SimpleApiRelation<'name'> | null;
  charger: SimpleApiRelation<'code'> | null;
  connector: SimpleApiRelation<'code'> | null;
  company_name?: string;
  station_name?: string;
  charger_code?: string;
  connector_code?: string;
}

interface ChargerNotificationsApiResponse {
  data: ChargerNotificationApiItem[];
  current_page: number;
  page_size: number;
  total_elements: number;
  total_pages: number;
  last: boolean;
  first: boolean;
  number_of_elements: number;
  empty: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ChargerNotificationsService {
  protected endpoint = 'charger-notifications';

  private apiUrl = `${environment.apiBaseUrl}/${environment.apiPrefixes.catalogs}/${environment.apiVersions.security}`;

  private baseUrl = `${this.apiUrl}/${this.endpoint}`;

  // BehaviorSubject para el contador de no leídas
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  // BehaviorSubject para notificaciones individuales (opcional)
  private notificationSubject = new BehaviorSubject<any | null>(null);
  public notification$ = this.notificationSubject.asObservable();

  constructor(private http: HttpClient) {}

  getAll(
    page: number = 1,
    perPage: number = 10,
    filters: { [key: string]: any } = {},
    sortField?: string,
    sortOrder: number = 1,
  ): Observable<PaginatedResponse<ChargerNotification>> {
    let params = new HttpParams()
      .set('page', (page - 1).toString())
      .set('size', perPage.toString());

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    // Agregar ordenamiento
    if (sortField) {
      const sortDirection = sortOrder === 1 ? 'asc' : 'desc';
      params = params.set('sort', `${sortField}:${sortDirection}`);
    }

    return this.http
      .get<ChargerNotificationsApiResponse>(this.baseUrl, { params })
      .pipe(
        map((response) => ({
          data: (response?.data || []).map((item) =>
            this.mapApiRespToModel(item),
          ),
          meta: {
            page: response.current_page + 1,
            per_page: response.page_size,
            total_pages: response.total_pages,
            total_items: response.total_elements,
          },
        })),
      );
  }

  markAsRead(id: number): Observable<any> {
    return this.http.put(`${this.baseUrl}/${id}/read`, null).pipe(
      map((response: any) => {
        const data = response?.data;

        this.notificationSubject.next(data);
        this.updateUnreadCount();

        return data;
      }),
    );
  }

  unreadCount(): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/unread-count`)
      .pipe(map((response: any) => response.data));
  }

  loadUnreadCount(): void {
    this.updateUnreadCount();
  }

  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  refreshUnreadCount(): void {
    this.updateUnreadCount();
  }

  private updateUnreadCount(): void {
    this.http
      .get(`${this.baseUrl}/unread-count`)
      .pipe(map((response: any) => response?.data?.unread_count || 0))
      .subscribe({
        next: (count) => {
          this.unreadCountSubject.next(count);
        },
        error: (err) => {
          console.error('Error al actualizar contador de no leídas:', err);
        },
      });
  }

  private mapApiRespToModel(
    item: ChargerNotificationApiItem,
  ): ChargerNotification {
    return {
      ...item,
      company_name: item.company?.name,
      station_name: item.station?.name,
      charger_code: item.charger?.code,
      connector_code: item.connector?.code,
    };
  }
}
